import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, Navigate, useSearchParams } from "react-router-dom";

import "../speakEasy.css";
import "./game.css";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../admin/sessionSlice";
import { listQuestions } from "../../admin/functions/questionsApi";
import { normalizeSpeakEasyCategory } from "../../functions/speakEasyCategories";
import { setBackButtonUrl } from "../uiSlice";
import useUnlockPageScroll from "../useUnlockPageScroll";
import questionBg from "./img/question.png";
import { fitTextToBox } from "./fitText";

/** Question carousel from old/game.php: one question of the chosen category at a time. */
export default function Game() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const category = normalizeSpeakEasyCategory(searchParams.get("category"));

  const { data: themeData, currentTheme } = useSelector((s) => s.theme);
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);

  const [questions, setQuestions] = useState(null); // null = loading
  const [loadError, setLoadError] = useState(false);
  const [index, setIndex] = useState(0);

  const boxRef = useRef(null);
  const textRef = useRef(null);

  useUnlockPageScroll();

  // Shrink long questions until they fit the (fixed-size) card. Refit on question
  // change, card resize, and once the web font has loaded (it changes text metrics).
  useLayoutEffect(() => {
    const fit = () => fitTextToBox(textRef.current);
    fit();
    let cancelled = false;
    document.fonts?.ready.then(() => !cancelled && fit());
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(fit) : null;
    if (ro && boxRef.current) ro.observe(boxRef.current);
    return () => {
      cancelled = true;
      ro?.disconnect();
    };
  }, [questions, index, loadError]);

  useEffect(() => {
    dispatch(setBackButtonUrl("/category"));
  }, [dispatch]);

  useEffect(() => {
    if (!category || !currentTheme || !adminToken) return undefined;
    let cancelled = false;
    setQuestions(null);
    setLoadError(false);
    setIndex(0);
    listQuestions({
      themeName: currentTheme,
      organizationId: organizationId || "admin",
      sessionId: sessionId || "admin",
      token: adminToken,
    })
      .then((rows) => {
        if (cancelled) return;
        setQuestions(
          rows
            .filter((q) => q.category === category)
            .map((q) => String(q.question_name ?? "").trim())
            .filter(Boolean)
        );
      })
      .catch((err) => {
        console.error("Game loadQuestions:", err);
        if (!cancelled) {
          setLoadError(true);
          setQuestions([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [category, currentTheme, adminToken, organizationId, sessionId]);

  if (!category) return <Navigate to="/category" replace />;

  let message;
  if (questions === null) message = "Loading question...";
  else if (loadError) message = "Failed to load questions.";
  else if (!questions.length) message = "No questions available.";
  else if (index >= questions.length) message = "You've reached the end of the questions.";
  else message = questions[index];

  const total = questions?.length || 0;
  const canGoNext = total > 0 && index < total;
  // Longer questions get a smaller font so the card never changes size (max 350 chars).
  const textSize = message.length > 200 ? "lg" : message.length > 120 ? "md" : "sm";

  return (
    <div
      className="se-game-page"
      style={{ "--se-question-mob-color": themeData?.sub_title_color || "#ffffff" }}
    >
      <div className="se-game-meta">
        <span className="se-game-category">{category}</span>
        {canGoNext ? (
          <span className="se-game-counter" aria-label={`Question ${index + 1} of ${total}`}>
            {index + 1} / {total}
          </span>
        ) : null}
      </div>

      <div
        ref={boxRef}
        className="se-question-box"
        style={{ backgroundImage: `url(${questionBg})` }}
      >
        {/* key restarts the pop-in animation for every new question */}
        <div className="se-question-inner">
          <p
            ref={textRef}
            key={`${index}-${message}`}
            className={`se-question-text se-question-text--${textSize}`}
            aria-live="polite"
          >
            {message}
          </p>
        </div>
      </div>

      <div className="se-game-actions">
        {canGoNext ? (
          <button type="button" className="se-btn" onClick={() => setIndex((i) => i + 1)}>
            <span>Next</span>
            <i className="fa-solid fa-angles-right se-btn-icon" aria-hidden="true" />
          </button>
        ) : null}
        <Link to="/category" className="se-btn se-btn--secondary">
          <i className="fa-solid fa-house se-btn-icon se-btn-icon--lead" aria-hidden="true" />
          <span>Home Page</span>
        </Link>
      </div>

      <div className="se-rotate-overlay" aria-hidden>
        Please rotate your device for the best experience
      </div>
    </div>
  );
}
